import { type Aabb, overlaps } from '@/combat/swing';
import { type BossDefinition, type BossPhase, phaseFor } from '@/data/bosses';
import type { Facing } from '@/sprites/katie';
import type { Tilemap } from '@/world/tilemap';

/**
 * The thing at the bottom of the stairs.
 *
 * A separate system from `EnemyForce` on purpose. A moth is a position and a
 * chase; a boss is a fight with a shape — it changes as it is hurt, it does
 * more than walk into you, and there is exactly one of it. Folding phases and
 * volleys into the enemy store would make every moth carry fields it will
 * never use, to describe behaviour it will never have.
 *
 * Free of rendering and input, like the rest of combat, so "does the second
 * phase actually shoot faster" is a question about numbers.
 */

export interface BossVolley {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  readonly damage: number;
  readonly count: number;
}

export class Boss {
  readonly definition: BossDefinition;
  x: number;
  y: number;
  health: number;
  /** Seconds of white flash after being hit. */
  flash = 0;
  private sinceVolley = 0;
  private pushX = 0;
  private pushY = 0;

  constructor(definition: BossDefinition, x: number, y: number) {
    this.definition = definition;
    this.x = x;
    this.y = y;
    this.health = definition.maxHealth;
  }

  get isDead(): boolean {
    return this.health <= 0;
  }

  /** How much fight is left, for the bar along the top of the screen. */
  get fraction(): number {
    return Math.max(0, this.health / this.definition.maxHealth);
  }

  get phase(): BossPhase {
    return phaseFor(this.definition, this.fraction);
  }

  /** Which of the phases this is, counting from 1, for the UI. */
  get phaseIndex(): number {
    return this.definition.phases.indexOf(this.phase) + 1;
  }

  hurtbox(): Aabb {
    const size = this.definition.radius * 2;
    return { x: this.x - this.definition.radius, y: this.y - this.definition.radius, width: size, height: size };
  }

  /**
   * Chase, and decide whether to shoot.
   *
   * Returns a volley when one is due rather than firing it, so the caller owns
   * the projectiles — the same split that lets the player's bolts and the
   * boss's be one system.
   */
  update(dt: number, targetX: number, targetY: number, map: Tilemap): BossVolley | null {
    if (this.isDead) return null;

    this.flash = Math.max(0, this.flash - dt);

    const phase = this.phase;
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const distance = Math.hypot(dx, dy) || 1;

    // Keeps its distance rather than sitting on top of you: a boss that
    // simply hugs the player is a damage-per-second check, not a fight.
    const approach = distance > this.definition.radius * 2.5 ? 1 : -0.35;

    let moveX = (dx / distance) * phase.speed * approach + this.pushX;
    let moveY = (dy / distance) * phase.speed * approach + this.pushY;

    const decay = Math.pow(0.0001, dt);
    this.pushX *= decay;
    this.pushY *= decay;

    this.step(moveX * dt, moveY * dt, map);
    moveX = 0;
    moveY = 0;

    this.sinceVolley += dt;
    if (this.sinceVolley < phase.volleySeconds) return null;
    this.sinceVolley = 0;

    return {
      x: this.x,
      y: this.y,
      facing: facingTowards(dx, dy),
      damage: phase.volleyDamage,
      count: phase.volleySize,
    };
  }

  private step(dx: number, dy: number, map: Tilemap): void {
    const r = this.definition.radius;
    const nextX = this.x + dx;
    if (!map.collides({ x: nextX - r, y: this.y - r, width: r * 2, height: r * 2 })) this.x = nextX;

    const nextY = this.y + dy;
    if (!map.collides({ x: this.x - r, y: nextY - r, width: r * 2, height: r * 2 })) this.y = nextY;
  }

  /** Returns true if the swing connected. */
  takeHit(arc: Aabb, damage: number, fromX: number, fromY: number): boolean {
    if (this.isDead || !overlaps(arc, this.hurtbox())) return false;

    this.health -= damage;
    this.flash = 0.12;

    // Knocked, but far less than a moth: it should give ground, not be kited
    // around the arena by a player who never stops swinging.
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const distance = Math.hypot(dx, dy) || 1;
    this.pushX = (dx / distance) * 24;
    this.pushY = (dy / distance) * 24;

    return true;
  }
}

function facingTowards(dx: number, dy: number): Facing {
  if (Math.abs(dy) >= Math.abs(dx)) return dy < 0 ? 'up' : 'down';
  return dx < 0 ? 'left' : 'right';
}
